import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Calendar, MapPin, Tag, Clock, ChevronRight, Loader, Search } from "lucide-react";
import SEO from "../components/SEO";

const CRM = process.env.REACT_APP_CRM_BACKEND;
const CRM_KEY = process.env.REACT_APP_CRM_API_KEY;

const CATEGORIES = ["All", "Academic", "Cultural", "Sports", "Holiday", "Admissions", "Celebration", "Event", "Workshop", "Other"];

const CATEGORY_COLORS = {
  Academic:    "bg-blue-50 text-blue-700 border-blue-200",
  Sports:      "bg-orange-50 text-orange-700 border-orange-200",
  Cultural:    "bg-purple-50 text-purple-700 border-purple-200",
  Holiday:     "bg-green-50 text-green-700 border-green-200",
  Admissions:  "bg-[#FDF5F6] text-[#9B1522] border-[#F8C5C9]",
  Celebration: "bg-pink-50 text-pink-700 border-pink-200",
  Event:       "bg-gray-100 text-gray-600 border-gray-200",
  Workshop:    "bg-teal-50 text-teal-700 border-teal-200",
  Other:       "bg-gray-100 text-gray-600 border-gray-200",
};

const normalizeEvent = (ev) => ({
  id:           ev.id || String(Math.random()),
  title:        ev.title || "",
  date:         ev.dateIso || ev.date || "",
  dateDisplay:  ev.date || ev.dateIso || "",
  time:         ev.time || "",
  description:  ev.description || "",
  category:     ev.type || ev.category || "Event",
  campus:       ev.campus || "All Campuses",
  image_url:    ev.imageUrl || ev.image_url || "",
  active:       ev.active !== false,
});

export default function Events() {
  const [all, setAll]         = useState([]);
  const [loading, setLoading] = useState(true);
  const [catFilter, setCat]   = useState("All");
  const [search, setSearch]   = useState("");

  useEffect(() => {
    axios
      .get(`${CRM}/api/public/events/tipsblr`, { headers: { "X-API-Key": CRM_KEY } })
      .then((res) => {
        if (Array.isArray(res.data) && res.data.length > 0) {
          setAll(res.data.map(normalizeEvent));
        } else {
          setAll([]); // CRM empty — no mock fallback
        }
      })
      .catch(() => setAll([]))  // CRM error — no mock fallback
      .finally(() => setLoading(false));
  }, []);

  const today = new Date().toISOString().split("T")[0];

  const filtered = all.filter((ev) => {
    if (!ev.active) return false;
    if (catFilter !== "All" && ev.category !== catFilter) return false;
    if (search && !ev.title.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const upcoming = filtered.filter((ev) => {
    const d = ev.date || "";
    return d >= today || !d.match(/^\d{4}-/);  // include non-ISO dates too
  });
  const past = filtered.filter((ev) => {
    const d = ev.date || "";
    return d < today && d.match(/^\d{4}-/);
  });

  const EventCard = ({ ev }) => {
    const colorClass = CATEGORY_COLORS[ev.category] || CATEGORY_COLORS.Other;
    const dateObj = ev.date && ev.date.match(/^\d{4}-/) ? new Date(ev.date + "T00:00:00") : null;
    const displayDate = ev.dateDisplay || (dateObj ? dateObj.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" }) : ev.date);
    return (
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-lg transition-all group" data-testid="event-card">
        {ev.image_url && (
          <div className="h-44 overflow-hidden">
            <img
              src={ev.image_url} alt={ev.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              onError={(e) => { e.target.parentElement.style.display = "none"; }}
            />
          </div>
        )}
        <div className="p-5">
          <div className="flex items-start justify-between gap-2 mb-3">
            <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${colorClass}`}>{ev.category}</span>
            {dateObj && (
              <div className="text-center bg-gray-50 rounded-xl px-2.5 py-1 flex-shrink-0">
                <div className="text-xs font-bold text-[#B41C2C] uppercase leading-tight">
                  {dateObj.toLocaleDateString("en-IN", { month: "short" })}
                </div>
                <div className="text-lg font-extrabold text-gray-900 leading-none">{dateObj.getDate()}</div>
              </div>
            )}
          </div>
          <h3 className="font-bold text-gray-900 mb-2 group-hover:text-[#9B1522] transition-colors leading-snug">{ev.title}</h3>
          <div className="space-y-1 mb-3">
            {ev.time && (
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <Clock size={12} className="text-[#B41C2C]" /> {ev.time}
              </div>
            )}
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <MapPin size={12} className="text-[#B41C2C]" /> {ev.campus}
            </div>
          </div>
          {ev.description && (
            <p className="text-xs text-gray-500 leading-relaxed line-clamp-3">{ev.description}</p>
          )}
        </div>
      </div>
    );
  };

  return (
    <main>
      <SEO
        title="School Events & Calendar"
        description="Stay up to date with TIPS Bengaluru school events – academic, cultural, sports, holidays and admission events across all 6 Bangalore campuses."
        keywords="TIPS Bengaluru events, school calendar Bangalore, CBSE school events, school activities Bangalore, TIPS school upcoming events"
        canonical="/events"
      />
      {/* Header */}
      <section className="bg-[#1A0505] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-sm text-gray-400 mb-4">
            <Link to="/" className="hover:text-[#C83C4A] transition-colors">Home</Link>
            <span>/</span>
            <span className="text-white">Events</span>
          </div>
          <div>
              <h1 className="text-4xl sm:text-5xl font-bold text-white" style={{ fontFamily: "'Poppins', sans-serif" }}>
                School Events
              </h1>
              <p className="text-gray-400 mt-2">Stay up to date with everything happening at TIPS Bengaluru.</p>
            </div>
        </div>
      </section>

      {/* Filters */}
      <section className="sticky top-0 z-10 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center gap-3 flex-wrap">
            {/* Search */}
            <div className="relative flex-shrink-0">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search events…"
                className="pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#B41C2C] focus:ring-1 focus:ring-[#B41C2C] w-44"
              />
            </div>
            {/* Category pills */}
            <div className="flex gap-2 flex-wrap">
              {CATEGORIES.map((c) => (
                <button
                  key={c}
                  onClick={() => setCat(c)}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${
                    catFilter === c
                      ? "bg-[#9B1522] text-white border-[#9B1522]"
                      : "bg-white text-gray-600 border-gray-200 hover:border-[#B41C2C] hover:text-[#B41C2C]"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader size={32} className="animate-spin text-[#B41C2C]" />
          </div>
        ) : (
          <>
            {/* Upcoming */}
            {upcoming.length > 0 && (
              <section className="mb-14">
                <div className="flex items-center gap-3 mb-6">
                  <span className="w-3 h-3 rounded-full bg-[#B41C2C] animate-pulse" />
                  <h2 className="text-xl font-bold text-gray-900" style={{ fontFamily: "'Poppins', sans-serif" }}>
                    Upcoming Events <span className="text-gray-400 font-normal text-base">({upcoming.length})</span>
                  </h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                  {upcoming.map((ev) => <EventCard key={ev.id} ev={ev} />)}
                </div>
              </section>
            )}

            {/* Past */}
            {past.length > 0 && (
              <section>
                <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-3" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  <span className="w-3 h-3 rounded-full bg-gray-300" />
                  Past Events <span className="text-gray-400 font-normal text-base">({past.length})</span>
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 opacity-70">
                  {past.map((ev) => <EventCard key={ev.id} ev={ev} />)}
                </div>
              </section>
            )}

            {upcoming.length === 0 && past.length === 0 && (
              <div className="text-center py-20 text-gray-400">
                <Calendar size={48} className="mx-auto mb-4 opacity-30" />
                <p className="font-medium text-lg">No events found</p>
                <p className="text-sm mt-1">Try changing the filter or check back later.</p>
              </div>
            )}
          </>
        )}
      </div>
    </main>
  );
}